// Shared types for the malpediaRAG web client.
// Port of obsidian-plugin/src/api/types.ts, minus the PluginSettings Obsidian glue.

export type PipelineStep = 'planning' | 'retrieving' | 'reranking' | 'generating';

export interface RoutingEvent {
  mode: string;
  confidence: number;
  reasoning: string;
}

export interface StepDetail {
  step: string; // e.g. 'retrieving', 'graph_expanding', 'reranking'
  message: string;
  count?: number;
  ts?: number; // unix epoch seconds from backend (or client fallback)
}

export interface ProfileEvent {
  depth: string;
  top_k: number;
  top_sources: number;
  fusion_cap: number;
}

export interface PlanningEvent {
  source?: string; // 'heuristic' | 'llm'
  model?: string | null;
  query_type: string;
  entities: string[];
  temporal_hint: string | null;
  keywords: string[];
  strategies: { name: string; weight: number }[];
  reasoning: string;
  fallback?: boolean;
}

export type SubgraphLabel =
  | 'MalwareFamily'
  | 'ThreatActor'
  | 'IOC'
  | 'CVE'
  | 'Document'
  | 'AttackTechnique'
  | 'WebSource';

export interface SubgraphNode {
  id: string;
  label: SubgraphLabel;
  name: string;
  score?: number;
  strategy?: string;
  url?: string;
  properties?: Record<string, unknown>;
}

export interface EntityLink {
  name: string;        // canonical name (used to build href)
  label: string;
  url: string;
  aliases?: string[];  // additional surface forms that should link to `name`
}

export interface SubgraphEdge {
  source: string;
  target: string;
  rel_type: string;
  data_source?: string;
}

export interface GraphTraversalEvent {
  nodes: SubgraphNode[];
  edges: SubgraphEdge[];
}

export interface DetectedIOC {
  type: string;
  value: string;
  raw_value: string;
  start: number;
  end: number;
  canonical_id: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface SourceDoc {
  cite_key: string;
  score: number;
  title?: string;
  vault_path?: string;
  text?: string;
  url?: string;
  retrieved_by?: string;
}

export interface DocumentDetail {
  cite_key: string;
  title?: string;
  url?: string;
  date?: string;
  author?: string;
  organization?: string;
  vault_path?: string;
}

export interface ScoreThreshold {
  min_score: number | null;
  cut_score: number | null;
  cutoff_dropped: number;
  excluded_sources: SourceDoc[];
}

export interface StoredMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: SourceDoc[];
  completedSteps?: PipelineStep[];
  stepDetails?: StepDetail[];
  planningEvent?: PlanningEvent | null;
  routingEvent?: RoutingEvent | null;
  graphTraversalEvent?: GraphTraversalEvent | null;
  profileEvent?: ProfileEvent | null;
  scoreThreshold?: ScoreThreshold | null;
  queryDetectedIocs?: DetectedIOC[];
  responseDetectedIocs?: DetectedIOC[];
  turnId?: string;          // server-issued turn id (absent for temporary chats)
}

// ── Accumulated Investigation Subgraph ─────────────────────────────────────

export interface AccumulatedNode {
  id: string;
  label: SubgraphLabel;
  name: string;
  score?: number;
  strategy?: string;
  properties?: Record<string, unknown>;
  addedAtQueryIndex: number;
  lastSeenAtQueryIndex: number;
  addedAtTimestamp: number;
  sourceQueryIds: string[];
  pinned: boolean;
  position?: { x: number; y: number };
}

export interface AccumulatedEdge {
  id: string;
  source: string;
  target: string;
  rel_type: string;
  data_source?: string;
  addedAtQueryIndex: number;
  sourceQueryIds: string[];
  expanded?: boolean;
}

export interface AccumulatedGraph {
  nodes: AccumulatedNode[];
  edges: AccumulatedEdge[];
  queryCount: number;
  lastUpdated: number;
}

// ── Chat options / request knobs ───────────────────────────────────────────

export type ChatMode =
  | 'auto'
  | 'vector'
  | 'local'
  | 'global'
  | 'drift'
  | 'planned'
  | 'llm_only';

export type RetrievalProfile =
  | 'auto'
  | 'narrow'
  | 'normal'
  | 'broad'
  | 'exhaustive'
  | 'manual';

export interface ChatOptions {
  mode: ChatMode;
  llmBackend: 'openai' | 'anthropic' | 'openai_compatible' | 'fhgenie';
  llmModel: string;
  llmBaseUrl: string;
  plannerLlmBackend: 'openai' | 'anthropic' | 'openai_compatible' | 'fhgenie';
  plannerLlmBaseUrl: string;
  plannerLlmModel: string;
  retrievalProfile: RetrievalProfile;
  topK: number;
  maxContextTokens: number;
  iocPivot: boolean;
  webSearch: boolean;
  neighborExpansion: boolean;
  fullDocContext: boolean;
  useV2IocGraph: boolean;
  customTopSources: number;
  customFusionCap: number;
  customMaxChunksPerDoc: number;
}

export const DEFAULT_CHAT_OPTIONS: ChatOptions = {
  mode: 'auto',
  llmBackend: 'openai',
  llmModel: '',
  llmBaseUrl: '',
  plannerLlmBackend: 'openai',
  plannerLlmBaseUrl: '',
  plannerLlmModel: '',
  retrievalProfile: 'auto',
  topK: 10,
  maxContextTokens: 0,
  iocPivot: true,
  webSearch: false,
  neighborExpansion: false,
  fullDocContext: false,
  useV2IocGraph: false,
  customTopSources: 8,
  customFusionCap: 20,
  customMaxChunksPerDoc: 0,
};

export interface ServerSettings {
  llmBackend: string;
  llmModel: string;
  llmBaseUrl: string;
  hasLlmApiKey: boolean;
  llmApiKeyHint: string;
  plannerLlmBackend: string;
  plannerLlmModel: string;
  plannerLlmBaseUrl: string;
  hasPlannerLlmApiKey: boolean;
  plannerLlmApiKeyHint: string;
  mode: string;
  retrievalProfile: string;
  topK: number;
  maxContextTokens: number;
  iocPivot: boolean;
  isTestAccount?: boolean;
}

// ── Chat folder / project ────────────────────────────────────────────────

export interface ChatFolder {
  id: string;
  name: string;
  createdAt: number;
}

// ── Chat session persistence ─────────────────────────────────────────────

export interface ChatSession {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: StoredMessage[];
  accumulatedGraph?: AccumulatedGraph;
  folderId?: string;
  temporary?: boolean;
  serverChatId?: string;       // FastAPI chat_id; absent for temporary or pre-feedback chats
  serverIsSensitive?: boolean; // mirrors chats.is_sensitive on the server
}

// ── Saved graph persistence ─────────────────────────────────────────────

export interface SavedGraph {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  graph: AccumulatedGraph;
  seedEntity?: string;
  seedLabel?: string;
}

export interface SummaryResponse {
  cite_key: string;
  summary: string | null;
  topic?: string;
  actors?: string[];
  families?: string[];
}
